/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useEffect, useState } from 'react'
import { AsyncPaginate } from 'react-select-async-paginate'
import { ActionMeta, components, GroupBase, MultiValue as MultiValueType, MultiValueProps, OptionsOrGroups, SingleValue } from 'react-select'
import { dropDownSelectStyles } from '../SelectDropDown/SelectDropDownStyles'
import { CheckBox, CustomTooltipWithLabel } from '../globalExports'
import { Arrow, Icon } from '../globalIcons'
import './AsyncSelectDropDown.css'

export type AsyncOption = { id: string; label: string }

export interface LoadOptionsResult {
  options: AsyncOption[];
  hasMore: boolean;
  totalRowCount: number;
}

export interface AsyncSelectDropDownProps {
  selectDropDownId: string
  fetchOptions: (search: string, offset: number, limit: number) => Promise<LoadOptionsResult>
  selected: AsyncOption[]
  onSelectedChange: (selected: AsyncOption[]) => void
  isMultiSelect?: boolean
  placeholder?: string
  pageSize?: number
  debounceTimeout?: number
  isDisabled?: boolean
  isClearable?: boolean
  renderToBody?: boolean
  menuPlacement?: 'top' | 'bottom' | 'auto'
  dropDownClassName?: string
  showMultipleOptionInside?: boolean
}

const ALL_OPTION_ID = 'all'
const ALL_OPTION: AsyncOption = { id: ALL_OPTION_ID, label: 'All' }

const DropdownIndicator: FC<{ isMenuOpen: boolean }> = ({isMenuOpen}) => (
  <Arrow
    color="black"
    direction="down"
    rotateAngle={isMenuOpen ? 180 : 0}
  />
)

const MultiValueRemove = (props): React.ReactNode => (
  <components.MultiValueRemove {...props}>
    <Icon
      containerClassName="react__select__dropdown__cross__icon__wrap"
      iconClassName="react__select__dropdown__cross__icon"
    />
  </components.MultiValueRemove>
)

const Option = ( props: { isAllSelected?: boolean, hasExclusions?: boolean }): React.ReactNode => {
  const { innerProps, isSelected, label, data, selectProps, isAllSelected, hasExclusions } = props as any;
  const checked = data?.id === ALL_OPTION_ID ? isSelected && !hasExclusions : isAllSelected ? !isSelected : isSelected;

  return (
    <div className="rms__dropdown__option__wrap" {...innerProps}>
      {selectProps.isMulti && (
        <div className="rms__dropdown__option-checkbox">
          <CheckBox checked={checked} />
        </div>
      )}

      <div className={`rms__dropdown__option-label ${checked ? 'selected' : ''}`}>
        <CustomTooltipWithLabel
          label={label}
          labelId={data?.id}
        />
      </div>
    </div>
  )
}

interface MoreSelectedBadgeProps {
  items: string[]
}

const MoreSelectedBadge: FC<MoreSelectedBadgeProps> = ({ items }) => (
  <div
    className="async__dropdown__more-badge"
    title={items.join(', ')}
  >
    {`+ ${items.length}`}
  </div>
)

const CountBadge: FC<{ count: number; title?: string }> = ({ count, title }) => (
  <div
    className="async__dropdown__more-badge"
    title={title}
  >
    {`+ ${count}`}
  </div>
)

const MultiValue = (
  props: MultiValueProps<AsyncOption> & {
    showMultipleOptionInside?: boolean
    hasExclusions?: boolean
    firstNonExcluded?: AsyncOption
    remainingCount?: number
  }
): React.ReactNode => {
  const { data, hasExclusions, firstNonExcluded, remainingCount } = props;

  if (hasExclusions) {
    /*
     * In exclusion mode:
     *
     * Actual value:
     * [All, excludedId1, excludedId2]
     *
     * But we display:
     * [firstNonExcluded, +N]
     *
     * React Select still internally thinks the first chip is "All".
     */

    if (data.id !== ALL_OPTION_ID || !firstNonExcluded) {
      return null
    }

    return (
      <>
        <components.MultiValue {...props} data={firstNonExcluded}>
          <CustomTooltipWithLabel
            label={firstNonExcluded.label}
            labelId={firstNonExcluded.id}
          />
        </components.MultiValue>

        {!!remainingCount && remainingCount > 0 && (
          <CountBadge count={remainingCount} />
        )}
      </>
    )
  }

  const { index, getValue, showMultipleOptionInside } = props;
  const maxToShow = showMultipleOptionInside ? 3 : 1;

  const overflow = getValue().slice(maxToShow).map((option) => option.label)

  if (index < maxToShow) {
    return (
      <components.MultiValue {...props}>
        <CustomTooltipWithLabel
          label={data.label}
          labelId={data.id}
        />
      </components.MultiValue>
    )
  }

  return index === maxToShow ? (
    <MoreSelectedBadge items={overflow} />
  ) : null
}

const AsyncSelectDropDown: FC<AsyncSelectDropDownProps> = ({
  selectDropDownId,
  fetchOptions,
  selected,
  onSelectedChange,
  isMultiSelect = true,
  placeholder = 'Select',
  pageSize = 50,
  debounceTimeout = 300,
  isDisabled = false,
  isClearable = true,
  renderToBody = false,
  menuPlacement = 'bottom',
  dropDownClassName = '',
  showMultipleOptionInside = false,
}) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  /*
   * Local selection is used so the UI updates immediately without
   * waiting for the parent/Redux state to update.
   */
  const [localSelected, setLocalSelected] = useState<AsyncOption[]>(selected ?? []);

  useEffect(() => {
    setLocalSelected(selected ?? [])
  }, [selected])

  /*
   * Keep track of all options loaded so far.
   *
   * This is required in exclusion mode because the actual selected
   * array only contains excluded IDs and therefore cannot tell us
   * which charger should be displayed as the representative chip.
   */
  const [seenOptions, setSeenOptions] = useState<AsyncOption[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const loadOptions = async (search: string,loadedOptions: OptionsOrGroups<AsyncOption,GroupBase<AsyncOption>>,additional?: { offset: number }) => {
    const offset = additional?.offset ?? 0;
    const { options, hasMore, totalRowCount: fetchedTotalCount } = await fetchOptions(search, offset, pageSize);
    const loadedIds = new Set((loadedOptions as AsyncOption[]).map((option) => option?.id));
    const newOptions = options.filter((option) => !loadedIds.has(option?.id));

    setSeenOptions((prev) => {
      const prevIds = new Set(prev.map((option) => option?.id));
      const additions = newOptions.filter((option) => !prevIds.has(option?.id));
      return additions.length > 0 ? [...prev, ...additions] : prev;
    })

    /*
     * Only the unfiltered total represents the total number
     * of chargers.
     */
    if (!search) {
      setTotalCount(fetchedTotalCount);
    }

    /*
     * Add "All" only to the first unfiltered page
     */
    const isFirstUnfilteredPage = offset === 0 && !search;
    const page = isMultiSelect && isFirstUnfilteredPage && newOptions.length > 0 ? [ALL_OPTION, ...newOptions] : newOptions;

    return {
      options: page,
      hasMore,
      additional: {
        offset: offset + options.length,
      },
    }
  }

  /*
   * Derived selection state.
   */
  const isAllSelected = isMultiSelect && localSelected.some((option) => option?.id === ALL_OPTION_ID);
  const hasExclusions = isAllSelected && localSelected.length > 1;
  const excludedCount = hasExclusions ? localSelected.length - 1 : 0;
  const excludedIds = hasExclusions ? new Set(localSelected.filter((option) => option?.id !== ALL_OPTION_ID).map((option) => option?.id)) : null
  const firstNonExcluded = excludedIds ? seenOptions.find((option) => !excludedIds.has(option?.id)) : undefined

  /*
   * Total chargers minus:
   * - excluded chargers
   * - the one charger displayed as the representative chip
   */
  const remainingCount = hasExclusions ? Math.max(0, totalCount - excludedCount - 1) : 0;

  const handleChange = (value: SingleValue<AsyncOption> | MultiValueType<AsyncOption>, actionMeta: ActionMeta<AsyncOption>): void => {
    let next = !value ? [] : Array.isArray(value) ? [...value] : [value];
    const inExcludeMode = localSelected.some((option) => option?.id === ALL_OPTION_ID);

    /*
     * Selecting "All":
     *
     * Any previous selection/exclusions are discarded.
     */
    if ( actionMeta.action === 'select-option' && actionMeta.option?.id === ALL_OPTION_ID) {
      next = [ALL_OPTION]
    }

    /*
     * IMPORTANT:
     *
     * In exclusion mode the first displayed chip is NOT actually
     * the firstNonExcluded charger in React Select's value.
     *
     * React Select thinks that chip is "All".
     *
     * Therefore removing that chip must mean:
     *
     *     exclude firstNonExcluded
     *
     * and NOT:
     *
     *     remove All
     */
    else if ( actionMeta.action === 'remove-value' && inExcludeMode && localSelected.length > 1) {
      if (firstNonExcluded && !localSelected.some((option) => option?.id === firstNonExcluded?.id)) {
        next = [...localSelected,firstNonExcluded]
      } else {
        next = localSelected
      }
    }

    /*
     * Normal deselect/remove handling.
     */
    else if (actionMeta.action === 'deselect-option' || actionMeta.action === 'remove-value') {
      const removed = actionMeta.option ?? actionMeta.removedValue;

      if (removed?.id === ALL_OPTION_ID) {
        /*
         * If exclusions exist, clicking All means:
         *
         *     All + exclusions -> All
         *
         * If only All exists:
         *
         *     All -> nothing
         */
        next = localSelected.length > 1 ? [ALL_OPTION] : []
      } else if (removed && inExcludeMode && !localSelected.some((option) => option?.id === removed?.id)) {
        /*
         * The representative chip isn't actually present
         * in localSelected, so React Select cannot remove it
         * normally.
         *
         * Removing it means excluding that charger.
         */
        next = [...localSelected,removed]
      }
    }

    setLocalSelected(next)
    onSelectedChange(next)
  }

  return (
    <div className={`rms__dropdown__wrap ${dropDownClassName}`}>
      <AsyncPaginate
        id={selectDropDownId}
        value={isMultiSelect ? localSelected : localSelected[0] ?? null}
        loadOptions={loadOptions}
        onChange={handleChange}
        getOptionValue={(option: AsyncOption) => option?.id}
        getOptionLabel={(option: AsyncOption) => option?.label}
        defaultOptions
        cacheUniqs={[isMultiSelect]}
        additional={{ offset: 0 }}
        debounceTimeout={debounceTimeout}
        isMulti={isMultiSelect}
        isDisabled={isDisabled}
        isClearable={isMultiSelect ? isClearable : false}
        closeMenuOnSelect={!isMultiSelect}
        hideSelectedOptions={false}
        blurInputOnSelect={false}
        placeholder={placeholder}
        menuPlacement={menuPlacement}
        menuPortalTarget={renderToBody ? document.body : null}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}
        styles={dropDownSelectStyles}
        classNamePrefix="rms__dropdown"
        components={{
          DropdownIndicator: () => (
            <DropdownIndicator
              isMenuOpen={menuIsOpen}
            />
          ),

          MultiValueRemove,

          Option: (optionProps) => (
            <Option
              {...optionProps}
              isAllSelected={isAllSelected}
              hasExclusions={hasExclusions}
            />
          ),

          MultiValue: (multiValueProps) => (
            <MultiValue
              {...multiValueProps}
              showMultipleOptionInside={
                showMultipleOptionInside
              }
              hasExclusions={hasExclusions}
              firstNonExcluded={
                firstNonExcluded
              }
              remainingCount={remainingCount}
            />
          ),
        }}
      />
    </div>
  )
}

export default AsyncSelectDropDown